import type { FC } from 'react';

import { Box, Fade, type SxProps, Typography, useMediaQuery } from '@mui/material';
import { colors } from '@truvhq/ui';
import { clsx } from 'clsx';

import { useSandboxContext } from 'shared/lib/sandbox/useSandboxContext';
import { useScreenSize } from 'shared/lib/screen/useScreenSize';

type SandboxHeaderProps = {
  absolute?: boolean;
  isEmulator?: boolean;
};

export const MOBILE_SANDBOX_BAR_HEIGHT = 32;

/**
 * On mobile the pill becomes a full-width bar that occupies real layout space, because there is no
 * roomy page header for it to float over. Use this to reserve that space above the content.
 */
export const useMobileSandboxBarHeight = () => {
  const { isSandboxOn } = useSandboxContext();
  const { isMobile } = useScreenSize();

  return isMobile && isSandboxOn ? MOBILE_SANDBOX_BAR_HEIGHT : 0;
};

const SandboxModeLabel = ({ suffix }: { suffix?: string }) => (
  <Typography className="whitespace-nowrap font-medium text-white" variant="body2">
    You're using <strong style={{ color: colors.orange.main }}>sandbox mode</strong>
    {suffix}
  </Typography>
);

export const SandboxHeader: FC<SandboxHeaderProps> = ({ absolute, isEmulator = false }) => {
  const { isSandboxOn } = useSandboxContext();
  const { isMobile } = useScreenSize();
  const isCompact = useMediaQuery('(max-width:1280px)');

  if (!isSandboxOn) {
    return null;
  }

  const bannerColor = 'black';

  if (isMobile) {
    return (
      <div
        className={clsx('pointer-events-none flex w-full items-center justify-center', { 'absolute z-20': absolute })}
        data-testid="sandbox-header"
        style={{ backgroundColor: bannerColor, height: MOBILE_SANDBOX_BAR_HEIGHT }}
      >
        <SandboxModeLabel />
      </div>
    );
  }

  const capBase: SxProps = {
    content: '""',
    position: 'absolute',
    top: '1px',
    height: '32px',
    width: '32px',
    backgroundColor: 'transparent',
  } as const;

  return (
    <Fade in={isSandboxOn} timeout={600}>
      <div
        className={clsx('pointer-events-none flex w-full flex-col items-center', {
          'absolute z-20': absolute,
        })}
        data-testid="sandbox-header"
      >
        <div className="h-0.5 w-full min-w-[197px]" style={{ backgroundColor: bannerColor }} />
        <Box
          className="mt-[-1px] relative flex h-[32px] items-center rounded-2xl justify-center px-4"
          sx={{
            color: bannerColor,
            backgroundColor: 'currentColor',
            width: 'fit-content',
            minWidth: 'max-content',
            '&::before': {
              ...capBase,
              right: '-32px',
              borderTopLeftRadius: '16px',
              boxShadow: '-12px -16px 0 0',
            },
            '&::after': {
              ...capBase,
              left: '-32px',
              borderTopRightRadius: '16px',
              boxShadow: '12px -16px 0 0',
            },
          }}
        >
          <SandboxModeLabel suffix={isCompact || isEmulator ? '' : '. No real transactions will be processed.'} />
        </Box>
      </div>
    </Fade>
  );
};
